import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { CheckCircle2, Loader2, Sparkles, XCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError, api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

export const Route = createFileRoute("/reset-password")({
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search.token as string) || "",
  }),
  component: ResetPasswordPage,
  head: () => ({
    meta: [{ title: "Choose a new password — SmsEmpire" }, { name: "robots", content: "noindex" }],
  }),
});

function ResetPasswordPage() {
  const { t } = useI18n();
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const password = String(data.get("password") ?? "");
    const confirmation = String(data.get("confirm") ?? "");

    if (password !== confirmation) {
      toast.error(t("auth.passwords_do_not_match"));
      return;
    }

    setSaving(true);
    try {
      await api.post(
        "/api/auth/reset-password",
        { token, new_password: password },
        { anonymous: true, skipAuthRedirect: true },
      );
      setDone(true);
      // The reset also invalidated every existing session, so there is nothing to
      // carry over — send them to a clean login.
      setTimeout(() => navigate({ to: "/auth", search: { tab: "login" } }), 2500);
    } catch (error) {
      const code = error instanceof ApiError ? error.code : undefined;
      const message =
        code === "TOKEN_EXPIRED"
          ? t("auth.reset_expired")
          : code === "INVALID_TOKEN"
            ? t("auth.reset_invalid")
            : code === "PASSWORD_TOO_SHORT"
              ? t("auth.password_too_short")
              : t("common.error");
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-primary shadow-glow">
            <Sparkles className="h-5 w-5 text-primary-foreground" aria-hidden />
          </div>
          <span className="text-lg font-bold tracking-tight">SmsEmpire</span>
        </Link>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-elegant">
          {!token ? (
            <div className="text-center">
              <XCircle className="mx-auto h-10 w-10 text-destructive" aria-hidden />
              <h1 className="mt-4 text-xl font-semibold">{t("auth.reset_invalid")}</h1>
              <Button asChild variant="outline" className="mt-6 w-full">
                <Link to="/forgot-password">{t("auth.request_new_link")}</Link>
              </Button>
            </div>
          ) : done ? (
            <div className="text-center">
              <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" aria-hidden />
              <h1 className="mt-4 text-xl font-semibold">{t("auth.reset_done_title")}</h1>
              <p className="mt-2 text-sm text-muted-foreground">{t("auth.reset_done_body")}</p>
            </div>
          ) : (
            <>
              <h1 className="text-xl font-semibold">{t("auth.reset_title")}</h1>
              <p className="mt-2 text-sm text-muted-foreground">{t("auth.reset_subtitle")}</p>
              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="rp-password">{t("auth.new_password")}</Label>
                  <Input
                    id="rp-password"
                    name="password"
                    type="password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    autoFocus
                    aria-describedby="rp-hint"
                  />
                  <p id="rp-hint" className="text-xs text-muted-foreground">
                    {t("auth.password_hint")}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rp-confirm">{t("auth.confirm_password")}</Label>
                  <Input
                    id="rp-confirm"
                    name="confirm"
                    type="password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={saving}
                  className="w-full bg-gradient-primary shadow-glow"
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                      {t("auth.saving")}
                    </>
                  ) : (
                    t("auth.set_new_password")
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
